import React, { useCallback } from 'react';
import { EXPERIMENTS } from '../../../constants';
import { useFocusGroupFieldEvent } from '../../../contexts/events-context';
import { useFieldActions } from '../../../contexts/field-actions-context';
import { useFieldValidation } from '../../../contexts/field-validation-context';
import { useForm } from '../../../contexts/form-context';
import { useExperiments } from '../../../sdk/components';
import { Form } from '../../../ui/form/components/form';
import { useFormFields } from '../../../ui/form/contexts/form-fields-context';
import { ADDRESS_TARGETS, fromAtlasAddress } from './address';
import { AddressAutofillProvider } from './address-autofill-context';
import { CountryCodeProvider } from './country-code-context';
import styles from './multiline-address.module.scss';
import { useMultilineAddressForm } from './use-multiline-address-form';
const NO_VALUES = {};
const NO_ERRORS = [];
const MultilineAddress = ({ id, form: _form, value, onChange, onFieldBlur, onFieldFocus, errors, FieldLayout, fieldSettings, label, validation, target, autocompleteEnabled, required, fieldType })=>{
    const { isExperimentEnabled } = useExperiments();
    const streetlessLocalityFallback = isExperimentEnabled(EXPERIMENTS.STREETLESS_LOCALITY_FALLBACK);
    const { validateFields: parentValidateFields } = useFieldValidation();
    const formSchema = useForm();
    const fields = useFormFields();
    const fieldActions = useFieldActions();
    const { form, isMultilineAddressField } = useMultilineAddressForm({
        id,
        target,
        fieldType,
        form: _form,
        country: value?.country,
        fieldSettings,
        validation,
        required
    });
    const handleAddressSelect = useCallback((atlasAddress)=>{
        if (!value) {
            return;
        }
        const address = fromAtlasAddress(atlasAddress, isMultilineAddressField, streetlessLocalityFallback);
        const newValue = {
            ...address,
            [ADDRESS_TARGETS.COUNTRY]: address[ADDRESS_TARGETS.COUNTRY] ?? value[ADDRESS_TARGETS.COUNTRY]
        };
        onChange?.(newValue);
    }, [
        onChange,
        value,
        isMultilineAddressField,
        streetlessLocalityFallback
    ]);
    const handleFieldChange = useCallback((_target, subFieldValue)=>{
        if (isMultilineAddressField && _target === ADDRESS_TARGETS.COUNTRY && value?.[_target] !== subFieldValue) {
            onChange?.({
                [ADDRESS_TARGETS.COUNTRY]: subFieldValue
            });
            return;
        }
        onChange?.({
            ...value,
            [_target]: subFieldValue
        });
    }, [
        onChange,
        value,
        isMultilineAddressField
    ]);
    const validateFields = useCallback(async (fieldsToValidate)=>{
        if (!isMultilineAddressField) {
            return parentValidateFields(fieldsToValidate);
        }
        return parentValidateFields([
            ...fieldsToValidate,
            {
                path: `${target}/${ADDRESS_TARGETS.COUNTRY}`,
                value: value?.country
            }
        ]);
    }, [
        value,
        target,
        parentValidateFields,
        isMultilineAddressField
    ]);
    useFocusGroupFieldEvent(id, ()=>{
        const firstError = errors?.[0];
        const field = form?.fields?.find((f)=>'target' in f && f.target === firstError?.errorPath);
        return field?.id;
    });
    if (!form) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(FieldLayout, {
        fieldId: id
    }, /*#__PURE__*/ React.createElement(CountryCodeProvider, {
        countryCode: value?.country
    }, /*#__PURE__*/ React.createElement(AddressAutofillProvider, {
        autocompleteEnabled: autocompleteEnabled,
        onAddressSelect: handleAddressSelect
    }, /*#__PURE__*/ React.createElement("fieldset", {
        className: styles.reset
    }, /*#__PURE__*/ React.createElement("legend", {
        className: styles.visuallyHidden
    }, label), /*#__PURE__*/ React.createElement(Form, {
        formSchema: formSchema,
        form: form,
        values: value ?? NO_VALUES,
        onFieldChange: handleFieldChange,
        onBlur: onFieldBlur,
        onFocus: onFieldFocus,
        errors: errors ?? NO_ERRORS,
        validateFields: validateFields,
        fields: fields,
        fieldActions: fieldActions
    })))));
};
export default MultilineAddress;

//# sourceMappingURL=multiline-address.js.map